import { Drawer, Table } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import SearchBox from 'components/molecules/SearchBox';
import EpochDatePicker from 'components/atoms/EpochDatePicker';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import useFilterState from 'components/molecules/TradePromoOptimization/CustomProductGroup/hooks/useFilterState';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, transformFiltersV3 } from 'utils/helperMethods';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useFetchPromoAuditLog } from 'hooks/tradePromoOptimization/audit/useFetchPromoAuditLog';
import {
    AuditLogColumnConfig,
    AuditLogFilterKeyConfig,
    AuditLogSortableColumn,
    PromoAuditLogRequest,
    PromoAuditLogRow,
} from 'types/tpo/auditLog';

const StyledChangeHistoryTable = styled(Table)`
    background: white;
    border-radius: 8px;

    .ant-table {
        background: transparent;
    }

    .ant-table-thead > tr > th {
        background: #f8f8fc !important;
        font-weight: 500;
        font-size: 13px;
        color: #4e4c55;
        border-bottom: 1px solid #e8e8e8;
        border-right: 1px solid #e8e8e8;
        padding: 12px 16px;
    }

    .ant-table-thead > tr > th:last-child {
        border-right: none;
    }

    .ant-table-tbody > tr > td {
        font-size: 13px;
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;
        border-right: 1px solid #f0f0f0;
        vertical-align: top;
        word-break: break-word;
    }

    .ant-table-tbody > tr > td:last-child {
        border-right: none;
    }

    .ant-table-tbody > tr:last-child > td {
        border-bottom: none;
    }

    .ant-table-tbody > tr:hover > td {
        background: #fafafa;
    }

    .ant-table-column-sorter {
        color: #bfbfbf;
    }

    .ant-table-column-sorter-up.active,
    .ant-table-column-sorter-down.active {
        color: #44338a;
    }
`;

interface ChangeHistoryDrawerProps {
    open: boolean;
    onClose: () => void;
    promoName?: string;
    eventPromoId: string;
    width?: number | string;
    topOffset?: number;
}

const PAGE_SIZE = 50;

const ChangeHistoryDrawer = ({
    open,
    onClose,
    promoName = 'Promo Name',
    eventPromoId,
    width = '100%',
    topOffset = 0,
}: ChangeHistoryDrawerProps) => {
    const { appConfig } = useContext(UserContext);
    const tpoConfig = useMemo(() => getConfig('tradePromoOptimization', appConfig), [appConfig]);
    const auditLogColumns = useMemo<AuditLogColumnConfig[]>(
        () => tpoConfig?.auditLogColumns ?? [],
        [tpoConfig],
    );
    const auditLogFilterKeys = useMemo<AuditLogFilterKeyConfig[]>(
        () => tpoConfig?.auditLogFilterKeys ?? [],
        [tpoConfig],
    );

    const {
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        selectedFilter,
        setSelectedFilter,
        tempApplyFilterObject,
        setTempApplyFilterObject,
        reset: resetFilters,
        onSave: saveFilters,
    } = useFilterState(auditLogFilterKeys);

    const [sortBy, setSortBy] = useState<AuditLogSortableColumn>('time_stamp');
    const [sortingOrder, setSortingOrder] = useState<'asc' | 'desc'>('desc');
    const [searchInput, setSearchInput] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');
    const [dateFrom, setDateFrom] = useState<number | null>(null);
    const [dateTo, setDateTo] = useState<number | null>(null);
    const [offset, setOffset] = useState<number>(0);

    // Any filter / sort / committed-search / date change resets pagination to the first page.
    useEffect(() => {
        setOffset(0);
    }, [sortBy, sortingOrder, searchKey, dateFrom, dateTo, eventPromoId, applyFilterObject]);

    // Reset all transient state when the drawer is closed so reopening
    // for a different promo starts fresh.
    useEffect(() => {
        if (!open) {
            setSortBy('time_stamp');
            setSortingOrder('desc');
            setSearchInput('');
            setSearchKey('');
            setDateFrom(null);
            setDateTo(null);
            setOffset(0);
            resetFilters();
        }
    }, [open, resetFilters]);

    // Initialize filter state on mount (and whenever the dimension config changes)
    // so allCheckedAttribute / applyFilterObject have a key for every filter.
    useEffect(() => {
        resetFilters();
    }, [resetFilters]);

    const requestFilters = useMemo(
        () => transformFiltersV3(applyFilterObject, auditLogFilterKeys),
        [applyFilterObject, auditLogFilterKeys],
    );

    // Pad end_date to end-of-day (UTC) so BE includes events on the picked date.
    const endDateInclusive = useMemo(() => (dateTo != null ? dateTo + 86399 : null), [dateTo]);

    const variables: PromoAuditLogRequest = useMemo(
        () => ({
            event_promo_id: eventPromoId,
            columns: auditLogColumns.map((c) => c.key),
            limit: PAGE_SIZE,
            offset,
            sort_by: sortBy,
            sorting_order: sortingOrder,
            start_date: dateFrom,
            end_date: endDateInclusive,
            search_key: searchKey,
            filters: requestFilters,
        }),
        [
            eventPromoId,
            auditLogColumns,
            offset,
            sortBy,
            sortingOrder,
            dateFrom,
            endDateInclusive,
            searchKey,
            requestFilters,
        ],
    );

    const filterApiVariables = useMemo(() => {
        if (!selectedFilter?.value) return undefined;
        return {
            event_promo_id: eventPromoId,
            filter_key: selectedFilter.value,
            start_date: dateFrom,
            end_date: endDateInclusive,
            limit: 50,
            filters: transformFiltersV3(tempApplyFilterObject, auditLogFilterKeys, selectedFilter.value),
        };
    }, [selectedFilter, eventPromoId, dateFrom, endDateInclusive, tempApplyFilterObject, auditLogFilterKeys]);

    const onFilterSave = useCallback(
        (resultObj: any) => {
            saveFilters(resultObj);
            setOffset(0);
        },
        [saveFilters],
    );

    const {
        loading,
        error,
        data: rows,
        count,
    } = useFetchPromoAuditLog({
        url: tradePromoOptimizationUrls.getPromoAuditChangesUrl,
        method: 'post',
        variables,
        queryKey: ['tpo-audit-promo-changes', variables],
        queryConfig: { enabled: open && Boolean(eventPromoId) },
    });

    const tableColumns: ColumnsType<PromoAuditLogRow> = useMemo(
        () =>
            auditLogColumns.map((c) => ({
                title: c.label,
                dataIndex: c.key,
                key: c.key,
                sorter: c.sortable,
                sortOrder: sortBy === c.key ? (sortingOrder === 'asc' ? 'ascend' : 'descend') : null,
                width: c.width,
                render: (value: any, record: PromoAuditLogRow) => {
                    if (c.dataType === 'datetime') {
                        return <span>{value ? dayjs.unix(value).format('MMM DD YYYY HH:mm') : '-'}</span>;
                    }
                    const isTimestampField =
                        (c.key === 'old_value' || c.key === 'new_value') &&
                        (record.field === 'cra_start_timestamp' ||
                            record.field === 'cra_end_timestamp' ||
                            record.field === 'promo_start_timestamp' ||
                            record.field === 'promo_end_timestamp');
                    if (isTimestampField) {
                        const numVal = Number(value);
                        return (
                            <span>{value && !isNaN(numVal) ? dayjs.unix(numVal).format('MMM DD YYYY HH:mm') : '-'}</span>
                        );
                    }
                    if (value === undefined || value === null || value === '') {
                        return <span style={{ color: '#9ca3af' }}>-</span>;
                    }
                    return <span>{value}</span>;
                },
            })),
        [auditLogColumns, sortBy, sortingOrder],
    );

    const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
        if (sorter?.columnKey && sorter?.order) {
            setSortBy(sorter.columnKey as AuditLogSortableColumn);
            setSortingOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        } else {
            setSortBy('time_stamp');
            setSortingOrder('desc');
        }
    };

    const rowsWithKey = useMemo(() => (rows ?? []).map((row) => ({ ...row, key: row.id })), [rows]);
    const hasMore = (rows?.length ?? 0) < count;

    const title = `${eventPromoId} - ${promoName} - Change History`;

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center w-full">
                    <span className="font-semibold text-base">{title}</span>
                    <CloseOutlined onClick={onClose} className="cursor-pointer text-gray-500 hover:text-gray-700" />
                </div>
            }
            placement="right"
            onClose={onClose}
            open={open}
            width={width}
            closable={false}
            bodyStyle={{ padding: '16px 24px' }}
            headerStyle={{ borderBottom: 'none', padding: '16px 24px' }}
            contentWrapperStyle={{ top: topOffset, height: `calc(100% - ${topOffset}px)` }}
            maskStyle={{ top: topOffset }}
        >
            {/* Filters Row */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Date From:</span>
                        <EpochDatePicker
                            value={dateFrom}
                            onChange={(value) => setDateFrom(value)}
                            format="MM/DD/YYYY"
                            placeholder="Select date"
                            classNames="!rounded-md"
                            style={{ width: 130 }}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Date To:</span>
                        <EpochDatePicker
                            value={dateTo}
                            onChange={(value) => setDateTo(value)}
                            format="MM/DD/YYYY"
                            placeholder="Select date"
                            classNames="!rounded-md"
                            style={{ width: 130 }}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <NewGlobalFilter
                        availableFilters={auditLogFilterKeys}
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetFilters}
                        onSubmitHandler={onFilterSave}
                        url={tradePromoOptimizationUrls.getPromoAuditFilterOptionsUrl}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={filterApiVariables}
                        filterDimensionInfo={{}}
                        showAttributePanelToLeft
                    />
                    <SearchBox
                        search={searchInput}
                        setSearch={setSearchInput}
                        onSearch={(val: string) => setSearchKey(val)}
                        placeholder="Search"
                        width={180}
                    />
                </div>
            </div>

            {/* Filter Ribbon */}
            <div className="mb-4">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetFilters}
                    onSubmitHandler={onFilterSave}
                    filterKeys={auditLogFilterKeys}
                    parentClassName="px-0"
                />
            </div>

            {/* Table with infinite scroll */}
            <InfiniteScroller
                style={{ overflowX: 'hidden' }}
                data={rowsWithKey}
                loading={loading}
                hasMore={hasMore || loading}
                height={`calc(100vh - ${topOffset + 220}px)`}
                onNext={() => {
                    if (loading) return;
                    setOffset((prev) => prev + PAGE_SIZE);
                }}
            >
                <StyledChangeHistoryTable
                    columns={tableColumns as ColumnsType<any>}
                    dataSource={rowsWithKey}
                    pagination={false}
                    size="small"
                    scroll={{ x: '100%' }}
                    onChange={handleTableChange}
                    showSorterTooltip={false}
                    style={{
                        opacity: loading ? 0.3 : 1,
                        pointerEvents: loading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: loading ? '' : error ? 'Failed to load change history' : 'No changes found',
                    }}
                />
            </InfiniteScroller>

            {/* Footer count */}
            <div className="mt-2 text-xs text-gray-500">
                Showing {rows?.length ?? 0} of {count} records
            </div>
        </Drawer>
    );
};

export default ChangeHistoryDrawer;
